import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// Re-using the dashboard skeleton's enhanced styling approach for consistency
const SkeletonBase = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<Skeleton
		className={cn('bg-slate-200 dark:bg-slate-800', className)} // Use slightly darker for better contrast
		{...props}
	/>
)

const TemplateCardSkeleton = () => (
	<Card className="relative overflow-hidden border-2 border-transparent bg-gradient-to-r from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
		<CardHeader className="space-y-1">
			<div className="flex items-center gap-3">
				<SkeletonBase className="h-10 w-10 rounded-lg" />
				<div className="space-y-1.5 flex-1">
					<SkeletonBase className="h-5 w-3/4 rounded" />
				</div>
			</div>
		</CardHeader>
		<CardContent className="space-y-4">
			<div className="space-y-1.5">
				<SkeletonBase className="h-4 w-full rounded" />
				<SkeletonBase className="h-4 w-5/6 rounded" />
			</div>
			<SkeletonBase className="h-9 w-full rounded-md" />
		</CardContent>
	</Card>
)

const WidgetCardSkeleton = () => (
	<Card className="overflow-hidden bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border-2 border-transparent">
		<CardHeader className="space-y-2">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<SkeletonBase className="h-10 w-10 rounded-lg" />
					<SkeletonBase className="h-6 w-32 rounded" />
				</div>
				<SkeletonBase className="h-5 w-16 rounded-full" />
			</div>
			<div className="space-y-1.5 pt-1">
				<SkeletonBase className="h-4 w-full rounded" />
				<SkeletonBase className="h-4 w-4/5 rounded" />
			</div>
		</CardHeader>
		<CardContent className="space-y-4">
			<SkeletonBase className="h-10 w-full rounded-lg" />
			<div className="flex flex-wrap gap-2">
				<SkeletonBase className="h-5 w-16 rounded-full" />
				<SkeletonBase className="h-5 w-20 rounded-full" />
			</div>
			<div className="flex gap-2 mt-2">
				<SkeletonBase className="h-9 flex-1 rounded-md" />
				<SkeletonBase className="h-9 w-9 rounded-md" />
				<SkeletonBase className="h-9 w-9 rounded-md" />
			</div>
		</CardContent>
	</Card>
)

export const WidgetsPageSkeleton = () => {
	return (
		<div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
			{/* Header Skeleton */}
			<div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6 sm:mb-8 p-6 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl shadow-lg">
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<SkeletonBase className="h-10 w-10 rounded-lg" />
						<SkeletonBase className="h-8 w-48 rounded" />
					</div>
					<SkeletonBase className="h-4 w-80 rounded" />
				</div>
				<SkeletonBase className="h-11 w-full sm:w-40 rounded-lg" />
			</div>

			{/* Filter Skeleton */}
			<div className="flex flex-col sm:flex-row gap-4 mb-8">
				<SkeletonBase className="h-10 flex-1 rounded-md" />
				<SkeletonBase className="h-10 w-full sm:w-[180px] rounded-md" />
			</div>

			{/* Templates Skeleton */}
			<div className="mb-12">
				<SkeletonBase className="h-6 w-40 mb-4 rounded" />
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
					<TemplateCardSkeleton />
					<TemplateCardSkeleton />
					<TemplateCardSkeleton />
				</div>
			</div>

			{/* Your Widgets Skeleton */}
			<div className="space-y-4">
				<SkeletonBase className="h-6 w-48 rounded" />
				<div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
					<WidgetCardSkeleton />
					<WidgetCardSkeleton />
					<WidgetCardSkeleton />
					<WidgetCardSkeleton />
					<WidgetCardSkeleton />
					<WidgetCardSkeleton />
				</div>
			</div>
		</div>
	)
}
